import { useCallback, useMemo, useRef, useState } from 'react';
import { centerViewport, continuousScale, fullViewport, panBy, splitScale, type AxisMode, type TimeScale, type Viewport, type ViewportLimits, zoomAt } from './scale.js';

export interface UseTimelineViewport {
  readonly scale: TimeScale;
  readonly viewport: Viewport;
  readonly mode: AxisMode;
  readonly centerOn: (year: number) => void;
  setMode(mode: AxisMode): void;
  zoom(factor: number, focusClientX: number, svgWidth: number): void;
  pan(deltaPx: number, svgWidth: number): void;
  reset(): void;
}

export interface ViewportOptions {
  readonly antiquity: readonly [number, number];
  readonly discovery: readonly [number, number];
  readonly width: number;
  readonly initialMode?: AxisMode;
  readonly minSpan?: number;
}

/**
 * Estado de pan e zoom.
 *
 * O zoom opera sobre o **dominio** (anos), nao sobre uma transformacao CSS: o
 * eixo partido tem duas regioes com escalas diferentes, e uma transformacao
 * unica deformaria a faixa de descoberta.
 */
export function useTimelineViewport(options: ViewportOptions): UseTimelineViewport {
  const [mode, setMode] = useState<AxisMode>(options.initialMode ?? 'split');

  const limits: ViewportLimits = useMemo(() => ({
    full: [options.antiquity[0], options.discovery[1]] as const,
    minSpan: options.minSpan ?? 5,
  }), [options.antiquity, options.discovery, options.minSpan]);

  const [viewport, setViewport] = useState<Viewport>(() => fullViewport(limits));
  const widthRef = useRef(options.width);
  widthRef.current = options.width;

  const scale = useMemo(() => {
    if (mode === 'continuous') {
      return continuousScale({ domain: viewport.domain, range: [0, options.width] });
    }
    // No modo partido, a janela de zoom aplica-se a Antiguidade; a faixa de
    // descoberta mantem-se estavel, porque comprimi-la nao ajudaria a ninguem.
    const lo = Math.max(viewport.domain[0], options.antiquity[0]);
    const hi = Math.min(Math.max(viewport.domain[1], lo + 1), options.antiquity[1]);
    return splitScale({
      antiquity: [lo, hi], discovery: options.discovery, range: [0, options.width],
    });
  }, [mode, viewport, options.width, options.antiquity, options.discovery]);

  const zoom = useCallback((factor: number, focusClientX: number, svgWidth: number) => {
    setViewport((v) => {
      const t = svgWidth === 0 ? 0.5 : focusClientX / svgWidth;
      const focusYear = v.domain[0] + t * (v.domain[1] - v.domain[0]);
      return zoomAt(v, limits, focusYear, factor);
    });
  }, [limits]);

  const pan = useCallback((deltaPx: number, svgWidth: number) => {
    setViewport((v) => {
      const yearsPerPx = (v.domain[1] - v.domain[0]) / Math.max(1, svgWidth);
      return panBy(v, limits, -deltaPx * yearsPerPx);
    });
  }, [limits]);

  const reset = useCallback(() => setViewport(fullViewport(limits)), [limits]);

  /** Centra a janela num ano, sem mudar o zoom; os limites continuam a valer. */
  const centerOn = useCallback((year: number) => {
    setViewport((v) => centerViewport(v, limits, year));
  }, [limits]);

  return { scale, viewport, mode, setMode, zoom, pan, reset, centerOn };
}
